import { useEffect, useState, useMemo, useCallback } from "react";
import { useAppSelector, useAppDispatch } from "../../../../redux/store";

import {
  Box,
  Drawer,
  Tabs,
  Tab,
  Typography,
  IconButton,
  Button,
  Alert,
  Divider,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import CloseIcon from "@mui/icons-material/Close";

import {
  closeDrawer,
  fetchCustomerById,
  createCustomer,
  updateCustomer,
} from "../../../../redux/slices/customersSlice";
import Info from "./Tabs/Info";
import HistoryTab from "./Tabs/ChangeLog";
import CoverImageUpload from "./CoverImageUpload";
import TeamTab from "./Tabs/Team";

const TabPanel = ({ children, value, index }: any) => (
  <div hidden={value !== index}>
    {value === index && <Box>{children}</Box>}
  </div>
);

export default function CustomersDrawer() {
  const {
    changes,
    drawerOpen,
    customerId,
    selectedCustomer,
    fetchCustomerError,
    createCustomerError,
    updateCustomerError,
    deleteCustomerError,
    updateCustomerStatus,
    createCustomerStatus,
    deleteCustomerStatus,
    fetchCustomerStatus,
    // setCoverImage,
  } = useAppSelector((state) => state.customers);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (drawerOpen && customerId !== "") {
      dispatch(fetchCustomerById(customerId));
    }
  }, [dispatch, drawerOpen, customerId]);

  const onClose = () => {
    dispatch(closeDrawer());
  };

  const [value, setValue] = useState(0);

  const handleChange = (_event: any, newValue: any) => setValue(newValue);

  const handleSubmit = useCallback(
    (e: any) => {
      e.preventDefault();

      if (customerId) {
        dispatch(
          updateCustomer({
            id: customerId,
            ...changes,
          })
        );
      } else {
        dispatch(
          createCustomer({
            ...changes,
          })
        );
      }
    },

    [dispatch, changes, customerId]
  );

  const isLoading = useMemo(() => {
    return (
      updateCustomerStatus === "loading" ||
      createCustomerStatus === "loading" ||
      fetchCustomerStatus === "loading" ||
      deleteCustomerStatus === "loading"
    );
  }, [
    updateCustomerStatus,
    createCustomerStatus,
    fetchCustomerStatus,
    deleteCustomerStatus,
  ]);

  return (
    <Drawer
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(19, 21, 23, 0.45)",
        },
        "& .MuiDrawer-paper": {
          mt: 7,
          backgroundColor: "var(--surface-container-lowest, #ffffff)",
          height: "calc(100% - 56px)",
          borderRadius: 0,
          border: "1px solid var(--outline-variant, #dcdcdc)",
          width: "100%",
          maxWidth: "900px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          transition: "transform 225ms cubic-bezier(0, 0, 0.2, 1)",
        },
      }}
      anchor="right"
      open={drawerOpen}
      onClose={onClose}
    >
      <Box
        sx={{
          px: { xs: 2.5, sm: 3 },
          py: { xs: 2, sm: 2.5 },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid var(--outline-variant, #dcdcdc)",
        }}
      >
        <Box
          sx={{
            minWidth: 0,
          }}
        >
          <Typography variant="h5" fontWeight="bold">
            {selectedCustomer
              ? "Edit Customer/Project"
              : "Add Customer/Project"}
          </Typography>
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box
        sx={{
          width: "100%",
          flex: 1,
          overflowY: "auto",
          px: { xs: 2.5, sm: 3 },
          py: { xs: 2.5, sm: 3 },
        }}
      >
          <Tabs value={value} onChange={handleChange} aria-label="info tabs" sx={{ mb: 2.5 }}>
            <Tab label="General Info" />
            {/* <Tab label="Authorized" /> */}
            <Tab label="Transactions" />
            <Tab label="History" />
            <Tab label="Team" />
          </Tabs>
          {createCustomerStatus === "succeeded" && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Customer/Project created successfully
            </Alert>
          )}
          {updateCustomerStatus === "succeeded" && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Customer/Project updated successfully
            </Alert>
          )}
          {deleteCustomerStatus === "succeeded" && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Customer/Project deleted successfully
            </Alert>
          )}

          {fetchCustomerError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {fetchCustomerError}
            </Alert>
          )}
          {createCustomerError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {createCustomerError}
            </Alert>
          )}
          {updateCustomerError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {updateCustomerError}
            </Alert>
          )}
          {deleteCustomerError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteCustomerError}
            </Alert>
          )}
          <TabPanel value={value} index={0}>
            <Info />
            <Divider sx={{ mt: 3, mb: 2.5 }} textAlign="left">
              Cover Image
            </Divider>
            <Grid container sx={{ justifyContent: "flex-end" }}>
              <Grid size={{ xs: 12, md: 12, lg: 12 }}>
                <CoverImageUpload
                  customerId={customerId}
                  currentBackground={selectedCustomer?.backgroundImage}
                />

                {/* <CoverImageUploader onImageSelect={setCoverImage} /> */}
              </Grid>
            </Grid>
          </TabPanel>
          {/* <TabPanel value={value} index={1}>
            <Authorized />
          </TabPanel> */}
          <TabPanel value={value} index={1}>
            <Typography> Transactions Content</Typography>
          </TabPanel>
          <TabPanel value={value} index={2}>
            <HistoryTab />
          </TabPanel>
          <TabPanel value={value} index={3}>
            <TeamTab />
          </TabPanel>

          <Divider sx={{ mt: 4, mb: 2.5 }} textAlign="right">
            Actions
          </Divider>
          <Grid container spacing={1.5} sx={{ justifyContent: "flex-end" }}>
            <Grid size={{ xs: 6, md: 3, lg: 3 }}>
              <Button
                fullWidth
                onClick={onClose}
                size="small"
                variant="outlined"
                color="primary"
                disabled={isLoading}
              >
                Cancel
              </Button>
            </Grid>
            <Grid size={{ xs: 6, md: 3, lg: 3 }}>
              <Button
                fullWidth
                onClick={handleSubmit}
                size="small"
                variant="contained"
                color="primary"
                disabled={isLoading}
              >
                Save
              </Button>
            </Grid>
          </Grid>
      </Box>
    </Drawer>
  );
}
